import React, { useEffect, useRef, useState } from 'react';

interface ParticlesBackgroundProps {
  color?: string;
  count?: number;
  speed?: number;
  opacity?: number;
  className?: string;
  particleType?: 'circle' | 'square' | 'triangle' | 'mixed';
  interactive?: boolean;
  gradient?: boolean;
}

const ParticlesBackground: React.FC<ParticlesBackgroundProps> = ({
  color = 'rgba(59, 130, 246, 0.8)',
  count = 40,
  speed = 0.8,
  opacity = 0.5,
  className = '',
  particleType = 'mixed',
  interactive = true,
  gradient = true
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [mousePosition, setMousePosition] = useState<{x: number, y: number} | null>(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    let animationFrameId: number;
    let particles: Particle[] = [];
    let hue = 0; // Para efeito de gradiente de cores
    
    // Classe para representar uma partícula
    class Particle {
      x: number;
      y: number;
      size: number;
      baseSize: number;
      speedX: number;
      speedY: number;
      opacity: number;
      color: string;
      type: 'circle' | 'square' | 'triangle';
      canvasWidth: number;
      canvasHeight: number;
      angle: number;
      rotationSpeed: number;
      
      constructor(width: number, height: number) {
        this.canvasWidth = width;
        this.canvasHeight = height;
        
        this.x = Math.random() * this.canvasWidth;
        this.y = Math.random() * this.canvasHeight;
        this.baseSize = Math.random() * 4 + 1;
        this.size = this.baseSize;
        this.speedX = (Math.random() - 0.5) * speed;
        this.speedY = (Math.random() - 0.5) * speed;
        this.opacity = Math.random() * opacity + 0.1;
        
        // Definir tipo de partícula
        if (particleType === 'mixed') {
          const types: ('circle' | 'square' | 'triangle')[] = ['circle', 'square', 'triangle'];
          this.type = types[Math.floor(Math.random() * types.length)];
        } else {
          this.type = particleType;
        }
        
        // Cor da partícula - permite gradiente ou cor única
        if (gradient) {
          const hueValue = (hue + Math.random() * 60) % 360;
          this.color = `hsla(${hueValue}, 80%, 60%, ${this.opacity})`;
        } else {
          this.color = color.replace(')', `, ${this.opacity})`);
        }
        
        // Propriedades para rotação
        this.angle = Math.random() * 360;
        this.rotationSpeed = (Math.random() - 0.5) * 2;
      }
      
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        this.angle += this.rotationSpeed;
        
        // Fazer a partícula reaparecer do outro lado quando sair da tela
        if (this.x > this.canvasWidth) this.x = 0;
        else if (this.x < 0) this.x = this.canvasWidth;
        
        if (this.y > this.canvasHeight) this.y = 0;
        else if (this.y < 0) this.y = this.canvasHeight;
        
        // Interação com o mouse se estiver disponível
        if (interactive && mousePosition) {
          const dx = this.x - mousePosition.x;
          const dy = this.y - mousePosition.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const maxDistance = 150;
          
          if (distance < maxDistance) {
            // Aumentar tamanho quando perto do mouse
            const scale = 1 + ((maxDistance - distance) / maxDistance);
            this.size = this.baseSize * scale;
            
            // Adicionar pequena força de repulsão
            const forceX = dx / distance * 0.5;
            const forceY = dy / distance * 0.5;
            this.speedX += forceX;
            this.speedY += forceY;
            
            // Limitar velocidade máxima
            const maxSpeed = 3;
            const currentSpeed = Math.sqrt(this.speedX * this.speedX + this.speedY * this.speedY);
            if (currentSpeed > maxSpeed) {
              this.speedX = (this.speedX / currentSpeed) * maxSpeed;
              this.speedY = (this.speedY / currentSpeed) * maxSpeed;
            }
          } else {
            // Gradualmente voltar ao tamanho normal
            this.size = this.baseSize;
          }
        }
      }
      
      draw(context: CanvasRenderingContext2D) {
        context.save();
        context.translate(this.x, this.y);
        context.rotate(this.angle * Math.PI / 180);
        
        // Desenhar diferentes tipos de partículas
        if (this.type === 'circle') {
          context.beginPath();
          context.arc(0, 0, this.size, 0, Math.PI * 2);
          context.fillStyle = this.color;
          context.fill();
        } else if (this.type === 'square') {
          context.fillStyle = this.color;
          context.fillRect(-this.size, -this.size, this.size * 2, this.size * 2);
        } else if (this.type === 'triangle') {
          context.beginPath();
          context.moveTo(0, -this.size);
          context.lineTo(-this.size, this.size);
          context.lineTo(this.size, this.size);
          context.closePath();
          context.fillStyle = this.color;
          context.fill();
        }
        
        context.restore();
      }
    }
    
    // Ajustar o tamanho do canvas para corresponder ao tamanho do elemento pai
    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      
      canvas.width = parent.offsetWidth;
      canvas.height = parent.offsetHeight;
      
      // Reinicializar partículas quando o tamanho mudar
      initParticles();
    };
    
    // Inicializar partículas
    const initParticles = () => {
      particles = [];
      for (let i = 0; i < count; i++) {
        particles.push(new Particle(canvas.width, canvas.height));
      }
    };
    
    // Função de animação
    const animate = () => {
      // Limpar canvas com alguma transparência para criar efeito de "trail"
      ctx.fillStyle = 'rgba(17, 24, 39, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Incrementar hue para efeito de gradiente se ativado
      if (gradient) {
        hue = (hue + 0.5) % 360;
      }
      
      // Atualizar e desenhar cada partícula
      particles.forEach(particle => {
        particle.update();
        particle.draw(ctx);
      });
      
      // Conectar partículas próximas com linhas
      connectParticles();
      
      animationFrameId = requestAnimationFrame(animate);
    };
    
    // Conectar partículas próximas com linhas
    const connectParticles = () => {
      const maxDistance = 120;
      
      for (let i = 0; i < particles.length; i++) {
        for (let j = i; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < maxDistance) {
            // Quanto mais próximas, mais opaca a linha
            const opacity = 1 - (distance / maxDistance);
            
            ctx.beginPath();
            
            // Linhas coloridas se gradiente estiver ativado
            if (gradient) {
              ctx.strokeStyle = `hsla(${hue}, 80%, 60%, ${opacity * 0.2})`;
            } else {
              ctx.strokeStyle = color.replace(')', `, ${opacity * 0.2})`);
            }
            
            ctx.lineWidth = 1;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
    };
    
    // Eventos do mouse para interatividade
    const handleMouseMove = (event: MouseEvent) => {
      if (!interactive) return;
      
      const rect = canvas.getBoundingClientRect();
      setMousePosition({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      });
    };
    
    const handleMouseLeave = () => {
      setMousePosition(null);
    };
    
    // Configurar o canvas e iniciar a animação
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    if (interactive) {
      canvas.addEventListener('mousemove', handleMouseMove);
      canvas.addEventListener('mouseleave', handleMouseLeave);
    }
    
    animate();
    
    // Limpar ao desmontar
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (interactive) {
        canvas.removeEventListener('mousemove', handleMouseMove);
        canvas.removeEventListener('mouseleave', handleMouseLeave);
      }
      cancelAnimationFrame(animationFrameId);
    };
  }, [color, count, speed, opacity, particleType, interactive, gradient, mousePosition]);
  
  return (
    <canvas 
      ref={canvasRef} 
      className={`absolute top-0 left-0 w-full h-full ${interactive ? 'cursor-pointer' : 'pointer-events-none'} ${className}`}
      style={{ zIndex: 0 }}
    />
  );
};

export default ParticlesBackground; 